import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map } from 'rxjs';

import {
  BLAZE_EDITOR_HIDDEN_CLASS,
  BLAZE_HIDDEN_CLASS,
  ColumnId,
  NavigationContainerVariantType,
  PropsOf,
  SlotIdOf,
} from '@blaze-runtime/engine-domain';
import {
  getBackground,
  getDirectionValueMapFromCssProperty,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
} from '@blaze-shared/elements-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { CanvasElementComponent, ComponentSlotData } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

@UntilDestroy()
@Component({
  selector: 'be-container',
  templateUrl: './container.component.html',
  styleUrls: ['./container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
// TODO: test
export class ContainerComponent
  extends CanvasElementBaseComponent<'container' | NavigationContainerVariantType>
  implements CanvasElementComponent<'container'>, OnChanges, OnInit
{
  @Input() slotsProps?: Record<SlotIdOf<'container'>, PropsOf<'container'>>;

  @HostBinding('style') hostStyles?: Record<string, string | undefined>;
  @HostBinding('style.--b-cnt-padding-top') paddingTop = '';
  @HostBinding('style.--b-cnt-padding-right') paddingRight = '';
  @HostBinding('style.--b-cnt-padding-bottom') paddingBottom = '';
  @HostBinding('style.--b-cnt-padding-left') paddingLeft = '';
  private slots$$ = new BehaviorSubject<
    ComponentSlotData<'container', PropsOf<'container'>> | undefined
  >(undefined);
  slots$ = this.slots$$.asObservable().pipe(distinctUntilChanged(isDeepEqual));

  override slots?: ComponentSlotData<'container', PropsOf<'container'>>;

  slotsExpressionVisibilityClass?: Record<ColumnId, string>;
  @HostBinding('class.vertical') get vertical(): boolean {
    return this.elementType === 'navSidebarContainer';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['props']) {
      this.createStylesForHost();
    }

    if (changes['slots']) {
      this.slots$$.next(this.slots);
    }
  }

  ngOnInit(): void {
    this.initializeSlotsVisibility();
  }

  makeStyles(props?: PropsOf<'container'>): Record<string, string | undefined> | null {
    const { style } = props || {};
    const { container } = style || {};
    if (!container) {
      return null;
    }

    const background: Record<string, any> = {};
    if (container?.fill?.selectorType === 'image') {
      background['background-image'] = getBackground(container.fill);
      background['background-size'] = container?.imageBehavior ?? 'cover';
    } else {
      background['background'] = getBackground(container.fill);
    }

    return {
      ...background,
      ...makeBorderStyles(container.outline, container.outlineWidth),
      borderRadius: container.radius ?? 0,
      padding: container.offset?.padding ?? '0px',
      ...makeContainerVisualEffectStyles(container.visualEffect),
    };
  }

  createStylesForHost(): void {
    const styles = this.makeStyles(this.props);
    if (!styles) {
      return;
    }

    const padding = getDirectionValueMapFromCssProperty(styles['padding'] || '0px');
    this.paddingTop = padding.top;
    this.paddingRight = padding.right;
    this.paddingBottom = padding.bottom;
    this.paddingLeft = padding.left;
    delete styles['padding'];

    this.hostStyles = styles;
  }

  createStylesForSlot(slotId: SlotIdOf<'container'>): Record<string, string | undefined> | null {
    const props = this.slotsProps?.[slotId];
    if (!props) {
      return null;
    }

    const styles = this.makeStyles(props);

    if (styles) {
      const padding = getDirectionValueMapFromCssProperty(styles['padding'] || '0px');
      styles['--b-cnt-padding-top'] = padding.top;
      styles['--b-cnt-padding-right'] = padding.right;
      styles['--b-cnt-padding-bottom'] = padding.bottom;
      styles['--b-cnt-padding-left'] = padding.left;
      delete styles['padding'];
    }

    return styles;
  }

  /**
   * Handles expression visibility for slots.
   * Viewport visiblity is handled in slot-node.directive.ts
   * This logic is not moved there because of performance.
   * @private
   */
  private initializeSlotsVisibility(): void {
    combineLatest({
      slots: this.slots$,
      context: this.runtimeContext$,
    })
      .pipe(
        untilDestroyed(this),
        map(({ slots, context }) => {
          if (!slots) {
            return {};
          }

          const { slotIds, slotNodesData } = slots;
          const hasItemsWithVisibilityExpressions = slotIds.some(
            (slotId) => !!slotNodesData[slotId].props?.content?.visibilityExpression,
          );
          const normalizedContext = hasItemsWithVisibilityExpressions
            ? this.normalizeLinkedParams(context)
            : undefined;
          const result: Record<ColumnId, string> = {};

          slotIds.forEach((slotId) => {
            const props = slotNodesData[slotId].props;
            const { visibilityExpression } = props?.content || {};

            if (
              visibilityExpression &&
              normalizedContext &&
              // Ignore normalization for better performance (the context is the same for all items)
              !this.evaluateExpression(visibilityExpression, normalizedContext, true)
            ) {
              // Visibility expression - partially visible in editor
              result[slotId] = this.isInEditor ? BLAZE_EDITOR_HIDDEN_CLASS : BLAZE_HIDDEN_CLASS;
            }
          });

          return result;
        }),
      )
      .subscribe((slotsVisibility) => {
        this.slotsExpressionVisibilityClass = slotsVisibility;
        this.cdr.markForCheck();
      });
  }
}
